import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { format, parse } from "date-fns";
import { CalendarIcon, Search, Eye } from "lucide-react";
import {
  Table,
  Pagination,
  Button,
  Form,
  Row,
  Col,
  Spinner,
  Alert,
  Stack,
  OverlayTrigger,
  Popover,
  PopoverBody,
  InputGroup,
  Modal,
} from "react-bootstrap";
import { useAuth } from "./Home1";

const BASE_API_URL = process.env.REACT_APP_API_URL;

const OverlayTriggerWrapper = React.forwardRef(
  ({ children, ...props }, ref) => (
    <span {...props} ref={ref}>
      {children}
    </span>
  ),
);

const MessageHistory = () => {
  const { user, token } = useAuth();
  const userId = user?.id;

  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [currentPage, setCurrentPage] = useState(1);
  const [fromDate, setFromDate] = useState(undefined);
  const [toDate, setToDate] = useState(undefined);
  const [searchTerm, setSearchTerm] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [selectedMsg, setSelectedMsg] = useState(null);

  const fetchData = useCallback(async () => {
    if (!userId || !token) return;
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${BASE_API_URL}/sentmessage`, {
        params: { userId },
        headers: { Authorization: `Bearer ${token}` },
      });
      setMessages(response.data || []);
    } catch (err) {
      setError("Failed to load message history.");
    } finally {
      setLoading(false);
    }
  }, [userId, token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const parseDbDate = (dateStr) => {
    if (!dateStr) return null;
    if (dateStr.includes("-") && dateStr.indexOf("-") === 4)
      return new Date(dateStr);
    try {
      return parse(dateStr.split(" ")[0], "dd/MM/yyyy", new Date());
    } catch (e) {
      return new Date(dateStr);
    }
  };

  const formatTableDate = (dateStr) => {
    const date = parseDbDate(dateStr);
    if (!date || isNaN(date)) return "—";
    const day = date.getDate();
    let suffix = "th";
    if (day % 10 === 1 && day !== 11) suffix = "st";
    else if (day % 10 === 2 && day !== 12) suffix = "nd";
    else if (day % 10 === 3 && day !== 13) suffix = "rd";
    return `${day}${suffix} ${format(date, "MMM, yy")}`;
  };

  const filteredMessages = messages.filter((msg) => {
    const msgDate = parseDbDate(msg.msg_created);
    const search = searchTerm.toLowerCase();
    let start = fromDate ? new Date(fromDate) : null;
    if (start) start.setHours(0, 0, 0, 0);
    let end = toDate ? new Date(toDate) : null;
    if (end) end.setHours(23, 59, 59, 999);

    const dateMatch =
      (!start || (msgDate && msgDate >= start)) &&
      (!end || (msgDate && msgDate <= end));

    const content = msg.msg_message?.toLowerCase() || "";
    const searchMatch =
      !searchTerm ||
      msg.msg_sender?.toLowerCase().includes(search) ||
      msg.msg_destination?.includes(searchTerm) ||
      content.includes(search);
    return dateMatch && searchMatch;
  });

  const sortedMessages = filteredMessages.sort(
    (a, b) => parseDbDate(b.msg_created) - parseDbDate(a.msg_created),
  );
  const currentMessages = sortedMessages.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage,
  );
  const totalPages = Math.ceil(filteredMessages.length / rowsPerPage);

  const handleViewMessage = (msg) => {
    setSelectedMsg(msg);
    setShowModal(true);
  };

  const renderCalendarPopover = (date, onSelect) => (
    <Popover id='popover-calendar'>
      <PopoverBody>
        <Form.Control
          type='date'
          onChange={(e) => {
            onSelect(e.target.value ? new Date(e.target.value) : undefined);
            setCurrentPage(1);
          }}
        />
      </PopoverBody>
    </Popover>
  );

  return (
    <div className='bg-white p-3 p-md-4 border rounded shadow-sm mt-2'>
      <h2 className='h4 mb-4 text-center fw-bold text-primary'>
        Message History
      </h2>

      <Row className='gy-3 mb-4 align-items-center'>
        <Col xs={12} lg={4}>
          <InputGroup size='sm'>
            <InputGroup.Text className='bg-light'>
              <Search size={14} />
            </InputGroup.Text>
            <Form.Control
              placeholder='Search history...'
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
            />
          </InputGroup>
        </Col>
        <Col xs={12} lg={8}>
          <Stack
            direction='horizontal'
            gap={2}
            className='justify-content-md-end flex-wrap'
          >
            <Form.Select
              size='sm'
              style={{ width: "80px" }}
              value={rowsPerPage}
              onChange={(e) => setRowsPerPage(Number(e.target.value))}
            >
              <option value='10'>10</option>
              <option value='20'>20</option>
              <option value='50'>50</option>
            </Form.Select>

            <OverlayTrigger
              trigger='click'
              rootClose
              placement='bottom'
              overlay={renderCalendarPopover(fromDate, setFromDate)}
            >
              <OverlayTriggerWrapper>
                <Button
                  size='sm'
                  variant='outline-secondary'
                  className='bg-white'
                >
                  <CalendarIcon size={14} className='me-1' />{" "}
                  {fromDate ? format(fromDate, "dd MMM") : "From"}
                </Button>
              </OverlayTriggerWrapper>
            </OverlayTrigger>

            <OverlayTrigger
              trigger='click'
              rootClose
              placement='bottom'
              overlay={renderCalendarPopover(toDate, setToDate)}
            >
              <OverlayTriggerWrapper>
                <Button
                  size='sm'
                  variant='outline-secondary'
                  className='bg-white'
                >
                  <CalendarIcon size={14} className='me-1' />{" "}
                  {toDate ? format(toDate, "dd MMM") : "To"}
                </Button>
              </OverlayTriggerWrapper>
            </OverlayTrigger>

            <Button
              size='sm'
              variant='danger'
              onClick={() => {
                setFromDate(undefined);
                setToDate(undefined);
                setSearchTerm("");
              }}
            >
              Reset
            </Button>
          </Stack>
        </Col>
      </Row>

      {loading ? (
        <div className='text-center p-5'>
          <Spinner animation='border' variant='primary' />
        </div>
      ) : (
        <div className='table-responsive'>
          <Table striped bordered hover size='sm' className='mb-0'>
            <thead>
              <tr className='bg-primary text-white'>
                <th className='py-2 px-3'>Date</th>
                <th className='py-2 px-3'>Sender</th>
                <th className='py-2 px-3'>Recipient</th>
                <th className='py-2 px-3'>Message</th>
                <th className='py-2 text-center'>Status</th>
                <th className='py-2 text-center'>Action</th>
              </tr>
            </thead>
            <tbody>
              {currentMessages.map((msg, i) => (
                <tr key={i} style={{ fontSize: "0.85rem" }}>
                  <td className='px-3 text-nowrap text-secondary'>
                    {formatTableDate(msg.msg_created)}
                  </td>
                  <td className='fw-bold text-dark px-3'>{msg.msg_sender}</td>
                  <td className='px-3'>
                    {msg.msg_destination?.substring(0, 11)}..
                  </td>
                  <td
                    className='text-muted px-3'
                    style={{
                      maxWidth: "180px",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {msg.msg_message || "—"}
                  </td>
                  <td className='text-center'>
                    <span
                      className={`badge ${msg.msg_status?.toLowerCase().includes("sent") ? "bg-success" : "bg-danger"}`}
                      style={{ fontSize: "0.7rem" }}
                    >
                      {msg.msg_status}
                    </span>
                  </td>
                  <td className='text-center'>
                    <Button
                      variant='outline-primary'
                      size='sm'
                      className='py-0 px-2'
                      onClick={() => handleViewMessage(msg)}
                    >
                      <Eye size={13} className='me-1' /> View
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>

          {totalPages > 1 && (
            <div className='d-flex justify-content-center p-3 mt-2'>
              <Pagination size='sm' className='mb-0'>
                <Pagination.Prev
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                />
                <Pagination.Item active>{currentPage}</Pagination.Item>
                <Pagination.Next
                  onClick={() =>
                    setCurrentPage((p) => Math.min(totalPages, p + 1))
                  }
                  disabled={currentPage === totalPages}
                />
              </Pagination>
            </div>
          )}
        </div>
      )}

      {/* View Modal */}
      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        centered
        scrollable
      >
        <Modal.Header closeButton className='bg-primary text-white'>
          <Modal.Title className='h6'>Message Detail</Modal.Title>
        </Modal.Header>
        <Modal.Body className='bg-light'>
          {selectedMsg && (
            <div className='p-2'>
              <Row className='mb-3'>
                <Col xs={6}>
                  <small className='text-muted d-block'>Sent On:</small>
                  <span className='fw-bold' style={{ fontSize: "0.9rem" }}>
                    {selectedMsg.msg_created}
                  </span>
                </Col>
                <Col xs={6}>
                  <small className='text-muted d-block'>Status:</small>
                  <span
                    className={`badge ${selectedMsg.msg_status?.toLowerCase().includes("sent") ? "bg-success" : "bg-danger"}`}
                  >
                    {selectedMsg.msg_status}
                  </span>
                </Col>
              </Row>

              <div className='mb-3'>
                <small
                  className='text-muted d-block mb-1 text-uppercase fw-bold'
                  style={{ fontSize: "0.65rem" }}
                >
                  Recipients:
                </small>
                <div
                  className='p-2 bg-white border rounded shadow-sm'
                  style={{
                    maxHeight: "100px",
                    overflowY: "auto",
                    fontSize: "0.85rem",
                    wordBreak: "break-all",
                    lineHeight: "1.4",
                  }}
                >
                  {selectedMsg.msg_destination}
                </div>
              </div>

              <hr />

              <div className='bg-white p-3 rounded border shadow-sm'>
                <small
                  className='text-muted d-block mb-2 fw-bold text-uppercase'
                  style={{ fontSize: "0.7rem" }}
                >
                  Message Content
                </small>
                <div
                  style={{
                    whiteSpace: "pre-wrap",
                    fontSize: "0.9rem",
                    color: "#333",
                  }}
                >
                  {selectedMsg.msg_message}
                </div>
              </div>

              <div className='mt-3 text-end'>
                <small className='text-muted'>
                  Total Cost:{" "}
                  <strong>
                    {Number(selectedMsg.msg_units).toFixed(2)} units
                  </strong>
                </small>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant='secondary'
            size='sm'
            onClick={() => setShowModal(false)}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default MessageHistory;
